// Enhanced User Management Types
// Supporting the new user management and RBAC features

export interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'archived';
  timezone: string;
  phone?: string;
  avatar_url?: string;
  last_activity_at?: string;
  profile_completeness: number;
  suspension_reason?: string;
  suspension_expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UserActivity {
  id: string;
  user_id: string;
  session_id?: string;
  action_type: 'create' | 'read' | 'update' | 'delete' | 'login' | 'logout' | 'view' | 'export' | 'import';
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  details: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  duration_ms?: number;
  success: boolean;
  error_message?: string;
  created_at: string;
}

export interface UserSession {
  id: string;
  user_id: string;
  session_token: string;
  ip_address?: string;
  user_agent?: string;
  location: Record<string, any>;
  is_active: boolean;
  last_activity_at: string;
  expires_at?: string;
  created_at: string;
}

export interface FeatureUsage {
  id: string;
  user_id: string;
  feature_name: string;
  usage_count: number;
  last_used_at: string;
  created_at: string;
}

export interface UserInvitation {
  id: string;
  email: string;
  invited_by: string;
  role_ids: string[];
  client_ids?: string[];
  invitation_token: string;
  custom_message?: string;
  template_used?: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  accepted_at?: string;
  reminder_sent_count: number;
  last_reminder_sent_at?: string;
  created_at: string;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  notification_type: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  sms_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'weekly' | 'never';
  created_at: string;
  updated_at: string;
}

export interface LoginAttempt {
  id: string;
  email: string;
  ip_address: string;
  success: boolean;
  failure_reason?: string;
  user_agent?: string;
  created_at: string;
}

export interface RateLimit {
  id: string;
  identifier: string;
  action_type: string;
  attempt_count: number;
  window_start: string;
  blocked_until?: string;
}

export interface ContextualPermission {
  id: string;
  permission_id: string;
  context_type: 'client' | 'time' | 'ip' | 'data_scope';
  context_value: Record<string, any>;
  created_at: string;
}

export interface UserClientPermission {
  id: string;
  user_id: string;
  client_id: string;
  permission_id: string;
  granted_by?: string;
  expires_at?: string;
  created_at: string;
}

export interface RoleHierarchy {
  id: string;
  parent_role_id: string;
  child_role_id: string;
  inheritance_type: 'additive' | 'restrictive';
  created_at: string;
}

// Enhanced user with all related data
export interface EnhancedUserWithRoles {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'archived';
  timezone: string;
  phone?: string;
  avatar_url?: string;
  profile_completeness: number;
  last_activity_at?: string;
  roles: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  permissions?: Array<{
    id: string;
    name: string;
    resource: string;
    action: string;
    description: string;
  }>;
  clients?: Array<{
    id: string;
    name: string;
  }>;
  created_at: string;
  updated_at: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
  activity_count_30d?: number;
}

// User search and filtering
export interface UserSearchFilters {
  basic: {
    name?: string;
    email?: string;
    role?: string[];
    status?: string[];
  };
  advanced: {
    client_assignment?: string[];
    last_login_range?: {
      start: string;
      end: string;
    };
    created_date_range?: {
      start: string;
      end: string;
    };
    permission_level?: string;
    activity_level?: 'high' | 'medium' | 'low' | 'inactive';
    profile_completeness_min?: number;
  };
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: UserSearchFilters;
  is_public: boolean;
  created_by: string;
  created_at: string;
}

// Bulk operations
export interface BulkUserOperation {
  type: 'role_change' | 'status_change' | 'client_assignment' | 'password_reset' | 'delete';
  user_ids: string[];
  parameters: Record<string, any>;
}

export interface BulkOperationResult {
  success_count: number;
  failure_count: number;
  errors: Array<{
    user_id: string;
    error: string;
  }>;
}

// User analytics
export interface UserEngagementMetrics {
  daily_active_users: number;
  weekly_active_users: number;
  monthly_active_users: number;
  average_session_duration: number;
  feature_usage_stats: Record<string, number>;
  user_retention_rate: number;
  churn_risk_users: string[];
}

export interface UserGrowthMetrics {
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  growth_rate_weekly: number;
  growth_rate_monthly: number;
}

export interface RoleDistribution {
  role_name: string;
  user_count: number;
  percentage: number;
}

// Invitation templates
export interface InvitationTemplate {
  id: string;
  name: string;
  role_specific: boolean;
  subject_template: string;
  body_template: string;
  variables: string[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// User onboarding
export interface UserOnboardingStep {
  id: string;
  step_name: string;
  title: string;
  description: string;
  component: string;
  is_required: boolean;
  order: number;
  role_specific?: string[];
}

export interface UserOnboardingProgress {
  user_id: string;
  completed_steps: string[];
  current_step?: string;
  completion_percentage: number;
  started_at: string;
  completed_at?: string;
}

// Security and MFA
export interface MFAConfiguration {
  enabled: boolean;
  required_for_roles: string[];
  methods: {
    totp: boolean;
    sms: boolean;
    email: boolean;
    backup_codes: boolean;
  };
  grace_period_days: number;
}

export interface UserSecuritySettings {
  user_id: string;
  mfa_enabled: boolean;
  mfa_methods: string[];
  password_last_changed: string;
  login_notifications: boolean;
  session_timeout_minutes: number;
}

// API responses
export interface UserManagementResponse<T> {
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  filters_applied?: UserSearchFilters;
}

export interface UserActivityResponse {
  activities: UserActivity[];
  summary: {
    total_actions: number;
    unique_resources: number;
    most_active_day: string;
    top_actions: Array<{
      action_type: string;
      count: number;
    }>;
  };
}

// Form types
export interface CreateUserForm {
  email: string;
  first_name: string;
  last_name: string;
  role_ids: string[];
  client_ids?: string[];
  send_invitation: boolean;
  custom_message?: string;
  template_id?: string;
}

export interface UpdateUserForm {
  first_name?: string;
  last_name?: string;
  phone?: string;
  timezone?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'pending' | 'archived';
  suspension_reason?: string;
  suspension_expires_at?: string;
}

export interface UserRoleAssignment {
  user_id: string;
  role_ids: string[];
  client_ids?: string[];
  expires_at?: string;
}

// Notification types
export const NOTIFICATION_TYPES = {
  user_management: [
    'user_invited',
    'user_activated',
    'user_suspended',
    'role_changed',
    'password_reset_requested'
  ],
  security: [
    'login_from_new_device',
    'password_changed',
    'mfa_enabled',
    'suspicious_activity'
  ],
  system: [
    'maintenance_scheduled',
    'feature_updates',
    'system_alerts'
  ]
} as const;

export type NotificationType = 
  | typeof NOTIFICATION_TYPES.user_management[number]
  | typeof NOTIFICATION_TYPES.security[number]
  | typeof NOTIFICATION_TYPES.system[number];

// Feature names for tracking
export const FEATURE_NAMES = {
  user_management: 'user_management',
  role_management: 'role_management',
  campaign_creation: 'campaign_creation',
  artist_management: 'artist_management',
  client_management: 'client_management',
  report_generation: 'report_generation',
  data_upload: 'data_upload',
  budget_management: 'budget_management',
  integration_setup: 'integration_setup',
  dashboard_view: 'dashboard_view'
} as const;

export type FeatureName = typeof FEATURE_NAMES[keyof typeof FEATURE_NAMES];

// User status helpers
export const USER_STATUS_COLORS = {
  active: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-gray-100 text-gray-800 border-gray-200',
  suspended: 'bg-red-100 text-red-800 border-red-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  archived: 'bg-purple-100 text-purple-800 border-purple-200'
} as const;

export const USER_STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  pending: 'Pending',
  archived: 'Archived'
} as const;
